import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Sparkles, CheckCircle2, Loader2 } from "lucide-react";

/**
 * Global listener for the `order_ready` WebSocket event. When the agent's
 * order is marked Ready by admin, we open a prominent modal that they MUST
 * acknowledge before the parent Orders page unlocks the Close button.
 */
export default function ReadyPopup() {
  const [payload, setPayload] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const on = (e) => {
      const d = e.detail;
      if (d?.type !== "order_ready") return;
      setPayload(d);
    };
    window.addEventListener("crm:realtime", on);
    return () => window.removeEventListener("crm:realtime", on);
  }, []);

  const ack = async () => {
    if (!payload?.order_id) return;
    setBusy(true);
    try {
      await api.post(`/orders/${payload.order_id}/acknowledge`);
      toast.success("Acknowledged. You can now close the order.");
      // Fire a synthetic reload signal for open Orders pages.
      window.dispatchEvent(new CustomEvent("crm:realtime", { detail: { type: "order_updated" } }));
      setPayload(null);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={!!payload} onOpenChange={(v) => !busy && !v && setPayload(null)}>
      <DialogContent data-testid="order-ready-popup" className="max-w-sm border-purple-500/40">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display text-purple-700 dark:text-purple-300">
            <Sparkles className="h-5 w-5" /> Client file is READY
          </DialogTitle>
          <DialogDescription>
            Confirm you've received this update so you can close the order.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2 rounded-md border border-purple-500/40 bg-purple-500/10 p-3 text-sm">
          <div className="mono text-[11px] text-muted-foreground">{payload?.order_number}</div>
          <div className="font-semibold">{payload?.client_name}</div>
          <div className="mono text-xs text-muted-foreground">
            {payload?.client_number} · ₹{payload?.payment_amount?.toLocaleString?.() || payload?.payment_amount}
          </div>
          {payload?.data_type && <div className="text-xs">Data Type: <b>{payload.data_type}</b></div>}
        </div>
        <Button onClick={ack} disabled={busy} className="w-full rounded-full bg-purple-600 hover:bg-purple-700" data-testid="order-ready-ack-btn">
          {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-2 h-4 w-4" />}
          OK — Acknowledge
        </Button>
      </DialogContent>
    </Dialog>
  );
}
